import { Form, Row, Col, Button, FormRow, PopupHint } from '@op/opds.opux';
import { Slider } from '@op/opds.opux-slider';
import { Icon } from '@op/opds.opux-icons';
import { IconExpand, IconCollapse } from '@op/opds.opux-icons/navigation';
import { useState, Dispatch, SetStateAction } from 'react';
import { space } from '@op/opds-tokens';
import { Message, useMessage, useMessageGetter } from '@messageformat/react';
import ProfitAndSavingsComponent from './ProfitAndSavingsComponent';
import UserFormStepperRow from './UserFormStepperRow';
import { ProfitAndSavingsAnnouncer } from './ProfitAndSavingsAnnouncer';

interface Props {
  changeProfitExpectation: Dispatch<SetStateAction<number>>;
  setShowRecommendations: Dispatch<SetStateAction<boolean>>;
  isBannerLayout: boolean;
  isPersonCustomer: boolean;
}

export const UserForm = (props: Props): JSX.Element => {
  const {
    changeProfitExpectation,
    setShowRecommendations,
    isBannerLayout,
    isPersonCustomer,
  } = props;
  const [savingAmount, setSavingAmount] = useState(50);
  const [savingTime, setSavingTime] = useState(15);
  const [initialSaving, setInitialSaving] = useState(0);
  const [profitExpectation, setProfitExpectation] = useState(5);
  const [showGraph, setShowGraph] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [finalSum, setFinalSum] = useState(0);
  const [totalSaving, setTotalSaving] = useState(0);
  const [bonusSum, setBonusSum] = useState(0);
  const getTranslations = useMessageGetter('');
  const sliderAttrs = {
    marks: {
      0: '0',
      1: '1',
      2: '2',
      3: '3',
      4: '4',
      5: '5',
      6: '6',
      7: '7',
      8: '8',
      9: '9',
      10: '10',
    },
    steps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    min: 0,
    max: 10,
  };

  const userFormProfitExpectationLabel = useMessage('userForm_profitExpectation');
  const userFormPopupHint = useMessage('userForm_popupHint');

  const setIconForButton = (): Icon => {
    if (showGraph) {
      return IconCollapse;
    }
    return IconExpand;
  };

  return (
    <>
      <Form stacked>
        <Row style={{ paddingBottom: space.default }}>
          <UserFormStepperRow
            isBannerLayout={isBannerLayout}
            showGraph={showGraph}
            savingAmount={savingAmount}
            initialSaving={initialSaving}
            savingTime={savingTime}
            setSavingAmount={setSavingAmount}
            setInitialSaving={setInitialSaving}
            setSavingTime={setSavingTime}
            setDisabled={setDisabled}
          />
          {(!isBannerLayout || showGraph) && (
            <Col lg={8}>
              <FormRow
                label={userFormProfitExpectationLabel}
                hint={
                  <PopupHint label={getTranslations('profitExpectation_label')}>
                    {userFormPopupHint}
                  </PopupHint>
                }
                id="profitExpectation"
              >
                <Slider
                  marks={sliderAttrs.marks}
                  max={sliderAttrs.max}
                  min={sliderAttrs.min}
                  step={null}
                  defaultValue={profitExpectation}
                  onChange={(value: number | number[]) => {
                    if (typeof value === 'number') {
                      changeProfitExpectation(value);
                      setProfitExpectation(value);
                    }
                  }}
                />
              </FormRow>
            </Col>
          )}
          <Col
            lg={isBannerLayout && !showGraph ? 8 : 4}
            style={{ alignItems: 'center', display: 'flex' }}
          >
            <Button
              id="savingscalculator-show-graph-button"
              disabled={disabled}
              aria-expanded={showGraph}
              aria-controls="savingscalculator-profitGraph"
              primary
              icon={setIconForButton()}
              iconPos="right"
              className="savingscalculator-show-graph-button"
              size="large"
              type="button"
              onClick={() => {
                setShowRecommendations(!showGraph);
                return setShowGraph(!showGraph);
              }}
            >
              <Message id="userForm_checkSavingsAmount" />
            </Button>
          </Col>
        </Row>
      </Form>

      {isBannerLayout && showGraph && <hr />}

      {showGraph && (
        <section
          id="savingscalculator-profitGraph"
          aria-labelledby="savingscalculator-show-graph-button"
        >
          <ProfitAndSavingsComponent
            monthlySaving={savingAmount}
            savingTime={savingTime}
            initialSaving={initialSaving}
            profitExpectation={profitExpectation}
            isBannerLayout={isBannerLayout}
            isPersonCustomer={isPersonCustomer}
            onValuesChange={(final, total, bonus) => {
              setFinalSum(final);
              setTotalSaving(total);
              setBonusSum(bonus);
            }}
          />
        </section>
      )}

      <ProfitAndSavingsAnnouncer
        finalSum={finalSum}
        totalSaving={totalSaving}
        bonusSum={bonusSum}
        isPersonCustomer={isPersonCustomer}
        enabled={showGraph}
      />
    </>
  );
};

export default UserForm;
